"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import {
  Activity,
  Bluetooth,
  BluetoothOff,
  CheckCircle,
  Loader2,
  Lock as LockIcon,
  LockOpen,
  Timer,
} from "@/components/ui/icons";
import BaselineActionCard from "@/features/participant/components/BaselineActionCard";
import ParticipantFlowPanel from "@/features/participant/components/ParticipantFlowPanel";
import { useSensorStream } from "@/hooks/useSensorStream";
import { createGsrSignal } from "@/lib/gsr";
import { parseSensorPacket } from "@/lib/sensorPacket";
import { cn } from "@/lib/utils";

type BaselineValues = {
  gsrRawAdc: number | null;
  gsrSmoothAdc: number | null;
  gsrUiLevel: number | null;
};

type BaselineCaptureCardProps = {
  baseline: BaselineValues | null;
  hasBaseline: boolean;
  onBaselineCaptured: (values: BaselineValues) => void;
  onCaptureStateChange?: (isCapturing: boolean) => void;
  onClearBaseline: () => void;
  /** Optional override for baseline duration (in seconds). Default = 10s */
  durationSeconds?: number;
  canStartSession?: boolean;
  isStartingSession?: boolean;
  onStartSession?: () => void;
  startSessionMessage?: string;
  startError?: string | null;
  startSessionDisabledReason?: string;
  startSessionLabel?: string;
};

const DEFAULT_BASELINE_DURATION_SEC = 10;
const START_UNLOCK_ANIMATION_MS = 1400;
const baselineNeutralBorder = "color-mix(in srgb, var(--surface-border) 84%, transparent)";
const baselineNeutralSurface =
  "color-mix(in srgb, var(--surface-card) 86%, transparent)";
const baselineAccentBorder =
  "color-mix(in srgb, var(--session-accent-border) 72%, var(--surface-border) 28%)";
const baselineAccentSurface =
  "color-mix(in srgb, var(--session-accent-soft) 82%, var(--surface-card) 18%)";
const baselineAccentSurfaceStrong =
  "color-mix(in srgb, var(--session-accent-soft-strong) 72%, var(--surface-card) 28%)";
const baselineSuccessBorder =
  "color-mix(in srgb, var(--color-success) 18%, var(--surface-border) 82%)";
const baselineSuccessSurface =
  "color-mix(in srgb, var(--color-success) 10%, var(--surface-card) 90%)";
const baselineSuccessSurfaceStrong =
  "color-mix(in srgb, var(--color-success) 14%, var(--surface-card) 86%)";
const baselineWarningBorder =
  "color-mix(in srgb, var(--color-warning) 20%, var(--surface-border) 80%)";
const baselineWarningSurface =
  "color-mix(in srgb, var(--color-warning) 12%, var(--surface-card) 88%)";
const baselineErrorBorder =
  "color-mix(in srgb, var(--color-error) 34%, var(--surface-border) 66%)";
const baselineErrorSurface =
  "color-mix(in srgb, var(--color-error) 14%, var(--surface-card) 86%)";
const baselineIconSurface =
  "color-mix(in srgb, var(--surface-card) 90%, transparent)";
const baselineProgressSurface =
  "color-mix(in srgb, var(--surface-card) 92%, transparent)";
const baselineTextSecondary =
  "color-mix(in srgb, var(--color-foreground) 76%, var(--color-muted-foreground) 24%)";
const baselineTextMuted =
  "color-mix(in srgb, var(--color-foreground) 62%, var(--color-muted-foreground) 38%)";

function baselineSurfaceStyle(
  borderColor: string,
  background: string,
  color: string
) {
  return {
    borderColor,
    background,
    color,
  };
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function formatGsrUiLevel(value: number | null) {
  return value != null ? value.toFixed(1) : "—";
}

const BaselineCaptureCard: React.FC<BaselineCaptureCardProps> = ({
  baseline,
  hasBaseline,
  onBaselineCaptured,
  onCaptureStateChange,
  onClearBaseline,
  durationSeconds,
  canStartSession = false,
  isStartingSession = false,
  onStartSession,
  startSessionMessage,
  startError,
  startSessionDisabledReason,
  startSessionLabel,
}) => {
  const durationSec = durationSeconds ?? DEFAULT_BASELINE_DURATION_SEC;

  const {
    isSupported,
    supportMessage,
    isConnected,
    connect,
    lastSample,
    error,
  } = useSensorStream((line) => {
    const packet = parseSensorPacket(line);
    if (!packet) {
      console.warn("[BaselineCapture] Failed to parse sensor line:", line);
      return;
    }

    const signal = createGsrSignal({
      rawAdc: packet.gsrRaw,
      smoothAdc: packet.gsrSmooth ?? packet.gsrDisplay,
    });

    return {
      gsrRawAdc: signal.rawAdc ?? undefined,
      gsrSmoothAdc: signal.smoothAdc ?? undefined,
      gsrUiLevel: signal.uiLevel ?? undefined,
    };
  }, {
    trackLastSample: true,
  });

  // ---- STATE ----
  const [avgUiLevel, setAvgUiLevel] = useState<number | null>(null);

  const [isCapturing, setIsCapturing] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [hasCapturedOnce, setHasCapturedOnce] = useState(false);
  const [showStartUnlockAnimation, setShowStartUnlockAnimation] = useState(false);

  const [pendingBaseline, setPendingBaseline] = useState<BaselineValues | null>(null);

  // ---- ACCUMULATOR ----
  const sumsRef = useRef({
    rawAdcSum: 0,
    rawAdcCount: 0,
    smoothAdcSum: 0,
    smoothAdcCount: 0,
  });
  const sampleCountRef = useRef(0);

  const resetAccumulator = () => {
    sumsRef.current = {
      rawAdcSum: 0,
      rawAdcCount: 0,
      smoothAdcSum: 0,
      smoothAdcCount: 0,
    };
    sampleCountRef.current = 0;
  };

  const appendSample = (
    sample:
      | {
          gsrRawAdc?: number | null;
          gsrSmoothAdc?: number | null;
        }
      | null
      | undefined
  ) => {
    if (!sample) return false;

    const rawAdc = isFiniteNumber(sample.gsrRawAdc) ? sample.gsrRawAdc : null;
    const smoothAdc = isFiniteNumber(sample.gsrSmoothAdc)
      ? sample.gsrSmoothAdc
      : null;

    if (rawAdc == null && smoothAdc == null) {
      return false;
    }

    const sums = sumsRef.current;
    if (rawAdc != null) {
      sums.rawAdcSum += rawAdc;
      sums.rawAdcCount += 1;
    }
    if (smoothAdc != null) {
      sums.smoothAdcSum += smoothAdc;
      sums.smoothAdcCount += 1;
    }
    sampleCountRef.current += 1;
    return true;
  };

  const liveSignal = useMemo(() => {
    if (!lastSample) return null;
    return createGsrSignal({
      rawAdc: lastSample.gsrRawAdc,
      smoothAdc: lastSample.gsrSmoothAdc,
    });
  }, [lastSample]);

  // ---- COLLECT LIVE DATA ----
  useEffect(() => {
    if (!lastSample || !isCapturing) return;
    appendSample(lastSample);
  }, [lastSample, isCapturing]);

  // ---- TIMER ----
  useEffect(() => {
    if (!isCapturing) return;

    const durationMs = durationSec * 1000;
    const startedAt = Date.now();
    let finished = false;

    const finalizeCapture = () => {
      if (finished) return;
      finished = true;

      const { rawAdcSum, rawAdcCount, smoothAdcSum, smoothAdcCount } =
        sumsRef.current;

      const hasAnySamples = rawAdcCount > 0 || smoothAdcCount > 0;

      setElapsedMs(durationMs);
      if (!hasAnySamples) {
        console.warn("[BaselineCapture] No valid samples; ignoring baseline.");
        setIsCapturing(false);
        resetAccumulator();
        return;
      }

      const rawAdcAvg = rawAdcCount > 0 ? rawAdcSum / rawAdcCount : null;
      const smoothAdcAvg =
        smoothAdcCount > 0 ? smoothAdcSum / smoothAdcCount : rawAdcAvg;
      const baselineSignal = createGsrSignal({
        rawAdc: rawAdcAvg,
        smoothAdc: smoothAdcAvg,
      });

      const baselineValues: BaselineValues = {
        gsrRawAdc: baselineSignal.rawAdc,
        gsrSmoothAdc: baselineSignal.smoothAdc,
        gsrUiLevel: baselineSignal.uiLevel,
      };

      console.log("[BaselineCapture] Baseline captured:", {
        baseline: baselineValues,
        sampleCount: sampleCountRef.current,
      });

      setAvgUiLevel(baselineSignal.uiLevel);
      setHasCapturedOnce(true);
      setPendingBaseline(baselineValues);
      setIsCapturing(false);
      resetAccumulator();
    };

    const interval = window.setInterval(() => {
      if (finished) return;

      const nextElapsed = Math.min(Date.now() - startedAt, durationMs);
      setElapsedMs(nextElapsed);

      if (nextElapsed >= durationMs) {
        window.clearInterval(interval);
        finalizeCapture();
      }
    }, 200);

    return () => {
      finished = true;
      window.clearInterval(interval);
    };
  }, [isCapturing, durationSec]);

  // ---- COMMIT PARENT UPDATE ----
  useEffect(() => {
    if (!pendingBaseline) return;
    onBaselineCaptured(pendingBaseline);
  }, [pendingBaseline, onBaselineCaptured]);

  useEffect(() => {
    onCaptureStateChange?.(isCapturing);
  }, [isCapturing, onCaptureStateChange]);

  const wasStartReadyRef = useRef(canStartSession);
  const startUnlockAnimationFrameRef = useRef<number | null>(null);
  const startUnlockTimeoutRef = useRef<number | null>(null);

  const clearStartUnlockAnimationTimers = () => {
    if (startUnlockAnimationFrameRef.current !== null) {
      window.cancelAnimationFrame(startUnlockAnimationFrameRef.current);
      startUnlockAnimationFrameRef.current = null;
    }
    if (startUnlockTimeoutRef.current !== null) {
      window.clearTimeout(startUnlockTimeoutRef.current);
      startUnlockTimeoutRef.current = null;
    }
  };

  useEffect(() => {
    clearStartUnlockAnimationTimers();

    if (canStartSession && !wasStartReadyRef.current) {
      startUnlockAnimationFrameRef.current = window.requestAnimationFrame(() => {
        setShowStartUnlockAnimation(true);
        startUnlockAnimationFrameRef.current = null;
        startUnlockTimeoutRef.current = window.setTimeout(() => {
          setShowStartUnlockAnimation(false);
          startUnlockTimeoutRef.current = null;
        }, START_UNLOCK_ANIMATION_MS);
      });
    } else if (!canStartSession) {
      startUnlockAnimationFrameRef.current = window.requestAnimationFrame(() => {
        setShowStartUnlockAnimation(false);
        startUnlockAnimationFrameRef.current = null;
      });
    }

    wasStartReadyRef.current = canStartSession;
    return () => {
      clearStartUnlockAnimationTimers();
    };
  }, [canStartSession]);

  // ---- HELPERS ----
  const displayBaselineUiLevel = baseline?.gsrUiLevel ?? avgUiLevel ?? null;
  const hasBufferedReading =
    liveSignal?.rawAdc != null || liveSignal?.smoothAdc != null;
  const hasLiveReading = isConnected && hasBufferedReading;
  const liveHeaderGsrValue = hasLiveReading
    ? formatGsrUiLevel(liveSignal?.uiLevel ?? null)
    : "—";
  const hasDisplayBaseline =
    hasBaseline ||
    pendingBaseline !== null ||
    (hasCapturedOnce && displayBaselineUiLevel !== null);

  const progressRatio = useMemo(() => {
    if (durationSec <= 0) return 0;
    if (isCapturing) {
      return Math.min(elapsedMs / (durationSec * 1000), 1);
    }
    return hasDisplayBaseline ? 1 : 0;
  }, [durationSec, elapsedMs, hasDisplayBaseline, isCapturing]);
  const captureProgressPercent = Math.round(progressRatio * 100);

  const canStartCapture = isConnected && hasLiveReading;

  const clearDisplayedBaseline = () => {
    setAvgUiLevel(null);
    setHasCapturedOnce(false);
    setPendingBaseline(null);
    setElapsedMs(0);
    onClearBaseline();
  };

  const handleStartCapture = () => {
    if (!canStartCapture) return;
    if (hasDisplayBaseline) {
      clearDisplayedBaseline();
    }
    resetAccumulator();
    appendSample(lastSample);
    setElapsedMs(0);
    console.log("[BaselineCapture] Capture started:", {
      durationSec,
      initialSample: {
        gsrRawAdc: liveSignal?.rawAdc ?? null,
        gsrSmoothAdc: liveSignal?.smoothAdc ?? null,
        gsrUiLevel: liveSignal?.uiLevel ?? null,
      },
    });
    setIsCapturing(true);
  };

  const handleProgressBarPress = () => {
    if (isCapturing || isStartingSession || !canStartCapture) return;
    handleStartCapture();
  };

  const captureRemainingSeconds = Math.max(
    0,
    Math.ceil((durationSec * 1000 - elapsedMs) / 1000)
  );
  const canInitiateSensorSetup =
    isSupported && !isConnected && !isCapturing && !isStartingSession;
  const actionCardDescriptionStyle = {
    color: "color-mix(in srgb, currentColor 84%, var(--color-foreground) 16%)",
  };
  const actionCardIconStyle = {
    borderColor:
      "color-mix(in srgb, currentColor 26%, var(--surface-border) 74%)",
    background: baselineIconSurface,
  };
  const actionCardMetaStyle = {
    borderColor:
      "color-mix(in srgb, currentColor 24%, var(--surface-border) 76%)",
    background:
      "color-mix(in srgb, currentColor 12%, var(--surface-card) 88%)",
    color: "inherit",
  };
  const connectCardClass = canInitiateSensorSetup ? "cursor-pointer" : "cursor-default";
  const connectCardLabel = isConnected
    ? "Sensor Connected"
    : isSupported
      ? "Connect Sensor"
      : "Sensor Unavailable";
  const connectCardDetail = isConnected
    ? "Your sensor is paired."
    : isSupported
      ? "Pair your sensor first so you can record a 10-second resting baseline."
      : supportMessage || "Bluetooth LE is not available on this device.";
  const connectCardMetaLabel = isConnected
    ? "Connected"
    : isSupported
      ? "Step 1"
      : "Unavailable";
  const connectCardStyle = isConnected
    ? baselineSurfaceStyle(
        baselineSuccessBorder,
        baselineSuccessSurface,
        "color-mix(in srgb, var(--color-foreground) 84%, var(--color-success) 16%)"
      )
    : isSupported
      ? baselineSurfaceStyle(
          baselineAccentBorder,
          baselineAccentSurface,
          "var(--session-accent-ink)"
        )
      : baselineSurfaceStyle(
          baselineErrorBorder,
          baselineErrorSurface,
          "color-mix(in srgb, var(--color-foreground) 76%, var(--color-error) 24%)"
        );
  const recordButtonClass =
    canStartCapture && !isCapturing && !isStartingSession
      ? "cursor-pointer"
      : "cursor-default";
  const recordButtonFillClass =
    "absolute inset-y-0 left-0 h-full transition-[width] duration-200 ease-linear";
  const recordButtonEyebrow = isCapturing
    ? "Recording now"
    : hasDisplayBaseline
      ? "Baseline saved"
      : canStartCapture
        ? "Step 2 of 2"
        : isConnected
          ? "Sensor warming up"
        : isSupported
          ? "Step 2 of 2"
          : "Sensor unavailable";
  const recordButtonLabel = isCapturing
    ? `Recording ${captureRemainingSeconds}s`
    : hasDisplayBaseline
      ? "Retake Baseline"
      : canStartCapture
        ? `Record ${durationSec}s Baseline`
        : isConnected
          ? "Waiting for Signal"
        : isSupported
          ? "Record Baseline"
          : "Sensor Unavailable";
  const recordButtonDetail = isCapturing
    ? "Stay still while we capture your resting signal."
    : hasDisplayBaseline
      ? "Retake only if the sensor shifted or the signal looked off."
      : canStartCapture
        ? "Capture a calm resting baseline before the session begins."
        : isConnected
          ? "Hold still for a moment until a clean live reading appears."
        : isSupported
          ? "Complete Step 1 first to unlock baseline recording."
          : supportMessage || "Bluetooth LE is not available on this device.";
  const recordButtonMetaLabel = isCapturing
    ? `${captureProgressPercent}%`
    : hasDisplayBaseline
      ? "Saved"
      : canStartCapture
        ? `${durationSec}s`
        : isConnected
          ? "Stand By"
        : isSupported
          ? "Locked"
          : "Locked";
  const recordButtonIconClass = cn(
    "h-[1.15rem] w-[1.15rem]",
    isCapturing ? "motion-safe:animate-pulse" : null
  );
  const recordButtonStyle = isCapturing
    ? baselineSurfaceStyle(
        baselineAccentBorder,
        baselineAccentSurfaceStrong,
        "var(--session-accent-ink)"
      )
    : canStartCapture
      ? baselineSurfaceStyle(
          baselineAccentBorder,
          baselineAccentSurface,
          "var(--session-accent-ink)"
        )
      : hasDisplayBaseline
        ? baselineSurfaceStyle(
            baselineNeutralBorder,
            baselineNeutralSurface,
            baselineTextSecondary
          )
      : !isSupported
        ? baselineSurfaceStyle(
            baselineErrorBorder,
            baselineErrorSurface,
            "color-mix(in srgb, var(--color-foreground) 76%, var(--color-error) 24%)"
          )
        : baselineSurfaceStyle(
            baselineNeutralBorder,
            baselineNeutralSurface,
            baselineTextMuted
          );
  const hasStatusMessages = Boolean(
    startError || error || (!isSupported && supportMessage)
  );
  const startButtonHint =
    !canStartSession && !isStartingSession
      ? startSessionDisabledReason ?? startSessionMessage ?? null
      : null;
  const startButtonEyebrow = isStartingSession
    ? "Launching"
    : canStartSession
      ? "Ready"
      : "Locked";
  const startButtonClass = cn(
    "system-btn-default",
    isStartingSession
      ? "opacity-95"
      : showStartUnlockAnimation
        ? "ring-2 ring-[color:var(--color-success-border)]"
        : null
  );
  const startButtonIconClass = cn(
    "h-[1.1rem] w-[1.1rem] shrink-0",
    isStartingSession
      ? "animate-spin"
      : showStartUnlockAnimation
        ? "text-[color:var(--color-success-foreground)]"
        : null
  );
  const startButtonLabel = isStartingSession
    ? "Starting Session"
    : startSessionLabel ?? "Start Session";
  const startButtonDetail = isStartingSession
    ? "Opening your session now."
    : canStartSession
      ? "Baseline saved. Start whenever you are ready."
      : startButtonHint ?? "Record a baseline to unlock session start.";
  const startButtonMetaLabel = isStartingSession
    ? "Opening"
    : canStartSession
      ? "Go"
      : "Locked";
  const isLocalMp3Action = startSessionLabel === "Open MP3 player";
  const canUseStartButton = Boolean(
    onStartSession && canStartSession && !isStartingSession
  );
  const startButtonStyle =
    canStartSession || isStartingSession
      ? baselineSurfaceStyle(
          baselineSuccessBorder,
          baselineSuccessSurfaceStrong,
          "color-mix(in srgb, var(--color-foreground) 88%, var(--color-success) 12%)"
        )
      : baselineSurfaceStyle(
          baselineNeutralBorder,
          baselineNeutralSurface,
          baselineTextMuted
        );
  const statusMessageClassName =
    "baseline-status-message rounded-[var(--radius-md)] border px-3 py-2 text-[0.6875rem] leading-[1.45] 2xl:text-[0.75rem]";

  // ---- UI ----
  return (
    <ParticipantFlowPanel
      className="gap-0 overflow-hidden rounded-[var(--radius-lg)] px-0 py-0"
      data-baseline-capture=""
    >
      <div className="psone-dark-container theme-menu-header grid items-center gap-3 px-4 py-4 sm:px-5">
        <div className="flex min-w-0 items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-[1rem] font-[650] leading-[1.15] text-[color:var(--color-foreground)] 2xl:text-[1.125rem]">
              Resting Baseline
            </h3>
          </div>

          <div
            className="shrink-0 rounded-[var(--radius-md)] border px-3 py-2 text-right"
            style={
              hasLiveReading
                ? {
                    borderColor: baselineSuccessBorder,
                    background: baselineSuccessSurface,
                  }
                : isConnected
                  ? {
                      borderColor: baselineWarningBorder,
                      background: baselineWarningSurface,
                    }
                  : {
                      borderColor: baselineNeutralBorder,
                      background: baselineNeutralSurface,
                    }
            }
          >
            <div
              className="flex items-center justify-end gap-2 whitespace-nowrap"
              aria-label="Live GSR"
            >
              <span
                className="inline-flex items-center justify-center"
                style={{
                  color: hasLiveReading
                    ? "color-mix(in srgb, var(--color-foreground) 74%, var(--color-success) 26%)"
                    : isConnected
                      ? "color-mix(in srgb, var(--color-foreground) 72%, var(--color-warning) 28%)"
                      : baselineTextMuted,
                }}
              >
                <Activity className="h-[0.82rem] w-[0.82rem]" aria-hidden="true" />
                <span className="sr-only">Live GSR</span>
              </span>
              <span
                className="text-[0.95rem] font-[650] leading-none tabular-nums 2xl:text-[1rem]"
                style={{
                  color: hasLiveReading
                    ? "color-mix(in srgb, var(--color-foreground) 88%, var(--color-success) 12%)"
                    : isConnected
                      ? "color-mix(in srgb, var(--color-foreground) 84%, var(--color-warning) 16%)"
                      : baselineTextSecondary,
                }}
              >
                {liveHeaderGsrValue}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid content-start gap-4 px-4 py-4 sm:px-5">
        {hasStatusMessages ? (
          <div className="grid min-w-0 gap-[0.6rem]">
            {startError ? (
              <p
                className={statusMessageClassName}
                style={{
                  borderColor: baselineErrorBorder,
                  background: baselineErrorSurface,
                  color:
                    "color-mix(in srgb, var(--color-error) 62%, white 38%)",
                }}
              >
                {startError}
              </p>
            ) : null}

            {error ? (
              <p
                className={statusMessageClassName}
                style={{
                  borderColor: baselineErrorBorder,
                  background: baselineErrorSurface,
                  color:
                    "color-mix(in srgb, var(--color-error) 62%, white 38%)",
                }}
              >
                Bluetooth error: {error}
              </p>
            ) : null}

            {!isSupported && supportMessage ? (
              <p
                className={statusMessageClassName}
                style={{
                  borderColor: baselineErrorBorder,
                  background: baselineErrorSurface,
                  color:
                    "color-mix(in srgb, var(--color-error) 62%, white 38%)",
                }}
              >
                {supportMessage}
              </p>
            ) : null}
          </div>
        ) : null}

        <div className="grid min-w-0 gap-3">
          <div className="grid order-1">
            <BaselineActionCard
              className="psone-light-container"
              style={connectCardStyle}
              eyebrow="Step 1 of 2"
              label={connectCardLabel}
              description={connectCardDetail}
              metaLabel={connectCardMetaLabel}
              icon={
                isConnected ? (
                  <CheckCircle className="h-[1.15rem] w-[1.15rem]" aria-hidden="true" />
                ) : (
                  <Bluetooth className="h-[1.15rem] w-[1.15rem]" aria-hidden="true" />
                )
              }
              iconStyle={actionCardIconStyle}
              metaStyle={actionCardMetaStyle}
              metaButtonProps={{
                onClick: () => {
                  if (!canInitiateSensorSetup) return;
                  void connect();
                },
                disabled: !canInitiateSensorSetup,
                "aria-label": isConnected ? "Sensor connected" : "Connect sensor",
                className: cn(
                  "system-btn psone-pairing-button",
                  connectCardClass,
                ),
              }}
              descriptionStyle={actionCardDescriptionStyle}
            />
          </div>

          <div className="order-2 grid">
            <BaselineActionCard
              data-psone-progress-card=""
              className="psone-light-container"
              style={recordButtonStyle}
              eyebrow={recordButtonEyebrow}
              label={recordButtonLabel}
              description={recordButtonDetail}
              metaLabel={recordButtonMetaLabel}
              icon={
                isCapturing ? (
                  <Timer className={recordButtonIconClass} aria-hidden="true" />
                ) : hasDisplayBaseline ? (
                  <CheckCircle className={recordButtonIconClass} aria-hidden="true" />
                ) : isConnected ? (
                  <Activity className={recordButtonIconClass} aria-hidden="true" />
                ) : (
                  <BluetoothOff className={recordButtonIconClass} aria-hidden="true" />
                )
              }
              iconStyle={actionCardIconStyle}
              metaStyle={actionCardMetaStyle}
              metaButtonProps={{
                onClick: handleProgressBarPress,
                disabled:
                  !canStartCapture || isCapturing || isStartingSession,
                "aria-label": hasDisplayBaseline
                  ? `Retake ${durationSec}-second baseline`
                  : `Record ${durationSec}-second baseline`,
                className: cn(
                  "psone-baseline-button",
                  "system-btn",
                  hasDisplayBaseline
                    ? "psone-baseline-button-success"
                    : null,
                  recordButtonClass,
                ),
              }}
              descriptionStyle={actionCardDescriptionStyle}
              overlay={
                <div
                  className="absolute inset-0 overflow-hidden rounded-[var(--radius-lg)]"
                  role="progressbar"
                  data-psone-progress=""
                  aria-label="Baseline capture progress"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={captureProgressPercent}
                  style={{
                    background: baselineProgressSurface,
                  }}
                >
                  <div
                    className={recordButtonFillClass}
                    data-psone-progress-fill=""
                    style={{
                      width: `${captureProgressPercent}%`,
                      background: isCapturing
                        ? "color-mix(in srgb, var(--session-accent-soft-strong) 82%, transparent)"
                        : hasDisplayBaseline
                          ? "color-mix(in srgb, var(--color-success-soft) 94%, transparent)"
                          : "color-mix(in srgb, var(--session-accent-soft) 74%, transparent)",
                    }}
                  />
                </div>
              }
            />
          </div>

          <div className="order-3 grid">
            <BaselineActionCard
              onClick={isLocalMp3Action ? undefined : onStartSession}
              disabled={
                isLocalMp3Action
                  ? undefined
                  : !onStartSession || !canStartSession || isStartingSession
              }
              aria-busy={isLocalMp3Action ? undefined : isStartingSession}
              title={
                isLocalMp3Action ? undefined : startButtonHint ?? undefined
              }
              className={cn(
                isLocalMp3Action ? "psone-light-container" : null,
                startButtonClass,
              )}
              style={startButtonStyle}
              eyebrow={startButtonEyebrow}
              label={startButtonLabel}
              description={startButtonDetail}
              metaLabel={startButtonMetaLabel}
              icon={
                isStartingSession ? (
                  <Loader2 className={startButtonIconClass} aria-hidden="true" />
                ) : canStartSession ? (
                  <LockOpen className={startButtonIconClass} aria-hidden="true" />
                ) : (
                  <LockIcon className={startButtonIconClass} aria-hidden="true" />
                )
              }
              iconStyle={actionCardIconStyle}
              metaStyle={actionCardMetaStyle}
              metaButtonProps={
                isLocalMp3Action
                  ? {
                      onClick: onStartSession,
                      disabled: !canUseStartButton,
                      "aria-busy": isStartingSession,
                      title: startButtonHint ?? undefined,
                      className: cn(
                        "psone-player-button",
                        "system-btn-default",
                        canUseStartButton ? "cursor-pointer" : "cursor-default",
                      ),
                    }
                  : undefined
              }
              descriptionStyle={actionCardDescriptionStyle}
            />
          </div>
        </div>
      </div>
    </ParticipantFlowPanel>
  );
};

export default BaselineCaptureCard;
